import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Tooltip from "@mui/material/Tooltip";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import AlignHorizontalLeftRoundedIcon from "@mui/icons-material/AlignHorizontalLeftRounded";
import AlignHorizontalCenterRoundedIcon from "@mui/icons-material/AlignHorizontalCenterRounded";
import AlignHorizontalRightRoundedIcon from "@mui/icons-material/AlignHorizontalRightRounded";
import AlignVerticalTopRoundedIcon from "@mui/icons-material/AlignVerticalTopRounded";
import AlignVerticalCenterRoundedIcon from "@mui/icons-material/AlignVerticalCenterRounded";
import AlignVerticalBottomRoundedIcon from "@mui/icons-material/AlignVerticalBottomRounded";
import {
  ClientLogo, ClientLogoVariant, DEFAULT_BACKGROUND, DEFAULT_EDITS, LogoPlacement, LogoTarget,
  calculateLogoLayout, drawLogo, loadLogoImage, logoProfile, logoQuality, prepareLogo, readClientLogoVariant, renderLogo,
} from "./clientLogo";
import "./ClientLogoEditor.css";
import { FormTheme, formThemePreviewStyle } from "./formTheme";

interface Props {
  value: ClientLogo | null;
  enabled: boolean;
  clientName?: string;
  showJobOfferDetail?: boolean;
  contextFormId?: string;
  formTheme?: FormTheme | null;
  themeControls?: React.ReactNode;
  expanded?: boolean;
  previewHost?: HTMLElement | null;
  onChange: (value: ClientLogo | null) => void;
  onToggle: () => void;
  onBusyChange: (busy: boolean) => void;
}

type ActiveTool = "size" | "background" | "edits" | "guides" | null;
type SnapGuides = { x?: "left" | "center" | "right"; y?: "top" | "center" | "bottom" };

function ToolButton({ label, active = false, disabled = false, onClick, children }: {
  label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return <Tooltip title={label} arrow placement="top">
    <span><button type="button" className={`client-logo-tool${active ? " is-active" : ""}`} aria-label={label}
      disabled={disabled} onClick={onClick}>{children}</button></span>
  </Tooltip>;
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (color: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const valid = /^#[\da-f]{6}$/i.test(text);
  return <label className="client-logo-color">{label}<span>
    <input type="color" aria-label={`${label}: selector`} value={value} onChange={(e) => { setText(e.target.value); onChange(e.target.value); }} />
    <input type="text" aria-label={`${label}: hexadecimal`} value={text} maxLength={7} aria-invalid={!valid}
      onChange={(e) => { setText(e.target.value); if (/^#[\da-f]{6}$/i.test(e.target.value)) onChange(e.target.value); }}
      onBlur={() => { if (!valid) setText(value); }} />
  </span></label>;
}

const variantOf = (logo: ClientLogo | null, target: LogoTarget): ClientLogoVariant | null => {
  if (!logo) return null;
  return target === "desktop" ? logo : logo.mobile || null;
};
const replaceVariant = (logo: ClientLogo, target: LogoTarget, variant: ClientLogoVariant): ClientLogo =>
  target === "desktop" ? { ...logo, ...variant } : { ...logo, mobile: variant };
const clampVariant = (variant: ClientLogoVariant, placement: LogoPlacement, target: LogoTarget): ClientLogoVariant => {
  const size = logoProfile(placement, target);
  return { ...variant,
    x: Math.max(-size.width * .4, Math.min(size.width * .4, variant.x)),
    y: Math.max(-size.height * .4, Math.min(size.height * .4, variant.y)),
  };
};

type TransformSettings = { zoom: number; x: number; y: number; stretchX: number; stretchY: number };
type CanvasInteraction =
  | { kind: "move"; pointerId: number; clientX: number; clientY: number; start: TransformSettings; scaleX: number; scaleY: number }
  | { kind: "resize"; pointerId: number; centerX: number; centerY: number; startDistance: number; start: TransformSettings }
  | { kind: "stretch"; pointerId: number; axis: "x" | "y"; side: "n" | "e" | "s" | "w";
      startClient: number; startSize: number; coordinateScale: number; start: TransformSettings };

const transformOf = (variant: ClientLogoVariant): TransformSettings => ({
  zoom: variant.zoom, x: variant.x, y: variant.y,
  stretchX: variant.stretchX ?? 1, stretchY: variant.stretchY ?? 1,
});
const clampZoom = (zoom: number) => Math.max(.25, Math.min(2, zoom));
const clampStretch = (stretch: number) => Math.max(.25, Math.min(3, stretch));
const resizeHandles = [
  ["nw", "superior izquierda"], ["ne", "superior derecha"],
  ["sw", "inferior izquierda"], ["se", "inferior derecha"],
] as const;
const stretchHandles = [
  ["n", "superior", "y"], ["e", "derecho", "x"],
  ["s", "inferior", "y"], ["w", "izquierdo", "x"],
] as const;

export default function ClientLogoEditor({ value, enabled, clientName = "cliente seleccionado", showJobOfferDetail = true, contextFormId, formTheme = null, themeControls, expanded = false, previewHost = null, onChange, onToggle, onBusyChange }: Props) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const editableFrame = useRef<HTMLDivElement>(null);
  const selectionFrame = useRef<HTMLDivElement>(null);
  const decoded = useRef<HTMLImageElement | null>(null);
  const generation = useRef(0);
  const [target, setTarget] = useState<LogoTarget>("desktop");
  const [selectedPlacement, setSelectedPlacement] = useState<LogoPlacement>(value?.placement || "card");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dark, setDark] = useState(false);
  const [editorMode, setEditorMode] = useState<"edit" | "preview">("edit");
  const [activeTool, setActiveTool] = useState<ActiveTool>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [gridSize, setGridSize] = useState(50);
  const [snapGuides, setSnapGuides] = useState<SnapGuides>({});
  const [readySource, setReadySource] = useState("");
  const [draft, setDraft] = useState<ClientLogo | null>(null);
  const shown = draft || value;
  const placement = shown?.placement || selectedPlacement;
  const active = variantOf(shown, target);
  const profile = logoProfile(placement, target);
  const maxX = Math.round(profile.width * .4);
  const maxY = Math.round(profile.height * .4);
  const interaction = useRef<CanvasInteraction | null>(null);
  const liveTransform = useRef<TransformSettings | null>(null);
  const animationFrame = useRef<number | null>(null);
  const [previewTransform, setPreviewTransform] = useState<TransformSettings | null>(null);
  const previewActive = active && previewTransform ? { ...active, ...previewTransform } : active;
  const editorSessionId = useRef(globalThis.crypto?.randomUUID?.() || `logo-${Date.now()}-${Math.random().toString(16).slice(2)}`).current;

  const trackLogoEvent = (event: string, outcome: "INICIO" | "OK" | "ERROR" = "OK", detail: Record<string, unknown> = {}) => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;
    const userId = sessionStorage.getItem("usuarioId") || "";
    const snapshot = variantOf(draft || value, target);
    void fetch(`${baseUrl}/form/client-logo-event?userId=${encodeURIComponent(userId)}`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ event, outcome, contextFormId, editorSessionId, target, placement,
        fileName:snapshot?.fileName, width:snapshot?.width, height:snapshot?.height,
        zoom:snapshot?.zoom, x:snapshot?.x, y:snapshot?.y,
        stretchX:snapshot?.stretchX, stretchY:snapshot?.stretchY, ...detail }),
    }).catch(()=>undefined);
  };

  useEffect(() => { if (value?.placement) setSelectedPlacement(value.placement); }, [value?.placement]);
  useEffect(() => {
    decoded.current = null; setReadySource(""); setError("");
    let cancelled = false;
    if (active?.source) loadLogoImage(active.source).then((image) => {
      if (!cancelled) { decoded.current = image; setReadySource(active.source); }
    }).catch(() => { if (!cancelled) setError("No pudimos abrir la imagen guardada. Carga otra imagen."); });
    return () => { cancelled = true; };
  }, [active?.source, target]);
  useLayoutEffect(() => {
    if (canvas.current && decoded.current && previewActive) try {
      drawLogo(canvas.current, decoded.current, previewActive, placement, target, { compactBanner: editorMode === "preview" });
    }
    catch (failure) { setError(failure instanceof Error ? failure.message : "No pudimos mostrar la edición."); }
  }, [previewActive, enabled, readySource, placement, target, editorMode, expanded, previewHost]);
  useEffect(() => {
    interaction.current = null; liveTransform.current = null; setPreviewTransform(null);
    setSnapGuides({});
    setEditorMode("edit");
    if (animationFrame.current !== null) { cancelAnimationFrame(animationFrame.current); animationFrame.current = null; }
  }, [active?.source, placement, target]);
  useEffect(() => () => {
    generation.current++;
    if (animationFrame.current !== null) cancelAnimationFrame(animationFrame.current);
  }, []);

  const setWorking = (next: boolean) => { setBusy(next); onBusyChange(next || !!draft); };
  const changeSettings = (settings: Partial<Pick<ClientLogoVariant, "zoom" | "x" | "y" | "stretchX" | "stretchY" | "background" | "edits">>, eventName?: string) => {
    if (!shown || !active || !decoded.current) return;
    try {
      const nextVariant = { ...active, ...settings };
      nextVariant.image = renderLogo(decoded.current, nextVariant, placement, target);
      const next = replaceVariant(shown, target, nextVariant);
      if (draft) setDraft(next); else onChange(next);
      if (eventName) trackLogoEvent(eventName, "OK", { tool:eventName, zoom:nextVariant.zoom, x:nextVariant.x, y:nextVariant.y, stretchX:nextVariant.stretchX, stretchY:nextVariant.stretchY });
      setError("");
    } catch (failure) {
      const message = failure instanceof Error ? failure.message : "No pudimos aplicar la edición.";
      setError(message); trackLogoEvent(eventName || "TRANSFORM_APPLIED", "ERROR", { tool:eventName, message });
    }
  };

  // Pointer movement only redraws the canvas. PNG encoding happens once when the
  // interaction ends; this keeps large banners responsive while they are moved.
  const previewSettings = (settings: Partial<TransformSettings>) => {
    if (!active) return;
    const current = liveTransform.current || transformOf(active);
    const next = { ...current, ...settings };
    next.zoom = clampZoom(next.zoom);
    next.stretchX = clampStretch(next.stretchX);
    next.stretchY = clampStretch(next.stretchY);
    next.x = Math.max(-maxX, Math.min(maxX, next.x));
    next.y = Math.max(-maxY, Math.min(maxY, next.y));
    liveTransform.current = next;
    if (animationFrame.current === null) animationFrame.current = requestAnimationFrame(() => {
      animationFrame.current = null;
      if (liveTransform.current) setPreviewTransform({ ...liveTransform.current });
    });
  };
  const snapMovement = (settings: TransformSettings, toleranceX: number, toleranceY: number): TransformSettings => {
    if (!snapEnabled || !decoded.current) { setSnapGuides({}); return settings; }
    let x = showGrid ? Math.round(settings.x / gridSize) * gridSize : settings.x;
    let y = showGrid ? Math.round(settings.y / gridSize) * gridSize : settings.y;
    const prepared = prepareLogo(decoded.current, active?.edits || DEFAULT_EDITS);
    const layout = calculateLogoLayout(prepared.width, prepared.height, { ...settings, x, y }, placement, target, false);
    const horizontal = [
      { value: -(profile.width - layout.imageWidth) / 2, guide: "left" as const },
      { value: 0, guide: "center" as const },
      { value: (profile.width - layout.imageWidth) / 2, guide: "right" as const },
    ];
    const vertical = [
      { value: -(profile.height - layout.imageHeight) / 2, guide: "top" as const },
      { value: 0, guide: "center" as const },
      { value: (profile.height - layout.imageHeight) / 2, guide: "bottom" as const },
    ];
    const nearestX = horizontal.reduce((best, candidate) => Math.abs(candidate.value - settings.x) < Math.abs(best.value - settings.x) ? candidate : best);
    const nearestY = vertical.reduce((best, candidate) => Math.abs(candidate.value - settings.y) < Math.abs(best.value - settings.y) ? candidate : best);
    const guides: SnapGuides = {};
    if (Math.abs(nearestX.value - settings.x) <= toleranceX) { x = nearestX.value; guides.x = nearestX.guide; }
    if (Math.abs(nearestY.value - settings.y) <= toleranceY) { y = nearestY.value; guides.y = nearestY.guide; }
    setSnapGuides(guides);
    return { ...settings, x, y };
  };
  const alignArtwork = (axis: "x" | "y", edge: "start" | "center" | "end") => {
    if (!active || !decoded.current) return;
    const prepared = prepareLogo(decoded.current, active.edits);
    const layout = calculateLogoLayout(prepared.width, prepared.height, active, placement, target, false);
    if (axis === "x") {
      const halfGap = (profile.width - layout.imageWidth) / 2;
      const x = Math.max(-maxX, Math.min(maxX, edge === "start" ? -halfGap : edge === "end" ? halfGap : 0));
      setSnapGuides({ ...snapGuides, x: edge === "start" ? "left" : edge === "end" ? "right" : "center" });
      changeSettings({ x }, "ALIGNMENT_APPLIED");
    } else {
      const halfGap = (profile.height - layout.imageHeight) / 2;
      const y = Math.max(-maxY, Math.min(maxY, edge === "start" ? -halfGap : edge === "end" ? halfGap : 0));
      setSnapGuides({ ...snapGuides, y: edge === "start" ? "top" : edge === "end" ? "bottom" : "center" });
      changeSettings({ y }, "ALIGNMENT_APPLIED");
    }
  };
  const commitPreview = () => {
    const next = liveTransform.current;
    if (animationFrame.current !== null) { cancelAnimationFrame(animationFrame.current); animationFrame.current = null; }
    interaction.current = null; liveTransform.current = null; setPreviewTransform(null);
    if (next) changeSettings(next, "TRANSFORM_APPLIED");
  };
  const cancelPreview = () => {
    if (animationFrame.current !== null) { cancelAnimationFrame(animationFrame.current); animationFrame.current = null; }
    interaction.current = null; liveTransform.current = null; setPreviewTransform(null);
  };
  const beginMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (busy || !active || editorMode !== "edit") return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    const start = liveTransform.current || transformOf(active);
    interaction.current = { kind:"move", pointerId:event.pointerId, clientX:event.clientX, clientY:event.clientY, start,
      scaleX:profile.width/rect.width, scaleY:profile.height/rect.height };
    previewSettings(start);
  };
  const beginResize = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (busy || !active || !selectionFrame.current || editorMode !== "edit") return;
    event.preventDefault(); event.stopPropagation();
    const rect = selectionFrame.current.getBoundingClientRect();
    const centerX = rect.left + rect.width/2, centerY = rect.top + rect.height/2;
    event.currentTarget.setPointerCapture(event.pointerId);
    const start = liveTransform.current || transformOf(active);
    interaction.current = { kind:"resize", pointerId:event.pointerId, centerX, centerY,
      startDistance:Math.max(1,Math.hypot(event.clientX-centerX,event.clientY-centerY)), start };
    previewSettings(start);
  };
  const beginStretch = (event: React.PointerEvent<HTMLButtonElement>, side: "n" | "e" | "s" | "w", axis: "x" | "y") => {
    if (busy || !active || !selectionFrame.current || !editableFrame.current || editorMode !== "edit") return;
    event.preventDefault(); event.stopPropagation();
    const rect = selectionFrame.current.getBoundingClientRect();
    const frameRect = editableFrame.current.getBoundingClientRect();
    const pointer = axis === "x" ? event.clientX : event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
    const start = liveTransform.current || transformOf(active);
    interaction.current = { kind:"stretch", pointerId:event.pointerId, axis, side, startClient:pointer,
      startSize:Math.max(1,axis === "x" ? rect.width : rect.height),
      coordinateScale:axis === "x" ? profile.width/frameRect.width : profile.height/frameRect.height, start };
    previewSettings(start);
  };
  const updatePointer = (event: React.PointerEvent<HTMLElement>) => {
    const current = interaction.current;
    if (!current || current.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (current.kind === "move") {
      const next = snapMovement({ ...current.start,
        x:current.start.x+(event.clientX-current.clientX)*current.scaleX,
        y:current.start.y+(event.clientY-current.clientY)*current.scaleY,
      }, 9*current.scaleX, 9*current.scaleY);
      previewSettings(next);
    }
    else if (current.kind === "resize") previewSettings({ zoom:current.start.zoom*Math.hypot(event.clientX-current.centerX,event.clientY-current.centerY)/current.startDistance });
    else {
      const pointer = current.axis === "x" ? event.clientX : event.clientY;
      const outwardDirection = current.side === "e" || current.side === "s" ? 1 : -1;
      const startStretch = current.axis === "x" ? current.start.stretchX : current.start.stretchY;
      const nextStretch = clampStretch(startStretch*(1+outwardDirection*(pointer-current.startClient)/current.startSize));
      const effectiveGrowth = current.startSize*(nextStretch/startStretch-1);
      const centerShift = outwardDirection*effectiveGrowth*current.coordinateScale/2;
      previewSettings(current.axis === "x"
        ? { stretchX:nextStretch, x:current.start.x+centerShift }
        : { stretchY:nextStretch, y:current.start.y+centerShift });
    }
  };
  const finishPointer = (event: React.PointerEvent<HTMLElement>) => {
    if (interaction.current?.pointerId !== event.pointerId) return;
    commitPreview();
  };

  const upload = async (file?: File) => {
    if (!file) return;
    trackLogoEvent("UPLOAD_STARTED", "INICIO", { fileName:file.name, width:undefined, height:undefined, message:`Carga de ${file.type || "imagen"}` });
    const current = ++generation.current; setWorking(true); setError("");
    try {
      const variant = await readClientLogoVariant(file, placement, target);
      if (current !== generation.current) return;
      const next = target === "desktop"
        ? ({ ...(value || {}), ...variant, enabled: true, placement } as ClientLogo)
        : value ? { ...value, placement, mobile: variant } : null;
      if (next) {
        setDraft(null); onChange(next);
        trackLogoEvent("UPLOAD_COMPLETED", "OK", { fileName:file.name, width:variant.width, height:variant.height });
      }
    } catch (failure) {
      if (current === generation.current) {
        const message = failure instanceof Error ? failure.message : "No pudimos cargar la imagen.";
        setError(message); trackLogoEvent("UPLOAD_FAILED", "ERROR", { fileName:file.name, message });
      }
    } finally { if (current === generation.current) setWorking(false); }
  };

  const changePlacement = async (nextPlacement: LogoPlacement) => {
    setSelectedPlacement(nextPlacement);
    if (!value) return;
    const current = ++generation.current; setWorking(true); setError(""); setDraft(null);
    try {
      const desktopImage = await loadLogoImage(value.source);
      const desktop = clampVariant(value, nextPlacement, "desktop");
      let next: ClientLogo = { ...value, ...desktop, placement: nextPlacement, image: renderLogo(desktopImage, desktop, nextPlacement, "desktop") };
      if (value.mobile) {
        const mobileImage = await loadLogoImage(value.mobile.source);
        const mobile = clampVariant(value.mobile, nextPlacement, "mobile");
        next = { ...next, mobile: { ...mobile, image: renderLogo(mobileImage, mobile, nextPlacement, "mobile") } };
      }
      if (current === generation.current) { onChange(next); trackLogoEvent("PLACEMENT_CHANGED", "OK", { tool:nextPlacement }); }
    } catch (failure) {
      if (current === generation.current) {
        const message = failure instanceof Error ? failure.message : "No pudimos adaptar las imágenes a la nueva ubicación.";
        setError(message); trackLogoEvent("PLACEMENT_CHANGED", "ERROR", { tool:nextPlacement, message });
      }
    } finally { if (current === generation.current) setWorking(false); }
  };

  const adaptDesktopToMobile = async () => {
    if (!value) return;
    const current = ++generation.current; setWorking(true); setError("");
    try {
      const image = await loadLogoImage(value.source);
      const mobile: ClientLogoVariant = { source:value.source, fileName:value.fileName, width:value.width, height:value.height,
        zoom:value.zoom, x:0, y:0, stretchX:value.stretchX ?? 1, stretchY:value.stretchY ?? 1,
        background:value.background, edits:value.edits, image:"" };
      mobile.image = renderLogo(image, mobile, placement, "mobile");
      if (current === generation.current) { onChange({ ...value, placement, mobile }); setTarget("mobile"); trackLogoEvent("MOBILE_ADAPTED"); }
    } catch (failure) {
      if (current === generation.current) {
        const message = failure instanceof Error ? failure.message : "No pudimos adaptar la imagen de escritorio.";
        setError(message); trackLogoEvent("MOBILE_ADAPTED", "ERROR", { message });
      }
    } finally { if (current === generation.current) setWorking(false); }
  };

  let warning: string | null = null;
  let selectionStyle: React.CSSProperties | undefined;
  let publishedHeight = profile.height;
  if (previewActive && decoded.current) try {
    const prepared=prepareLogo(decoded.current,previewActive.edits);
    warning=logoQuality(prepared.width,prepared.height,previewActive.zoom,placement,target,previewActive.stretchX,previewActive.stretchY);
    const editLayout=calculateLogoLayout(prepared.width,prepared.height,previewActive,placement,target,false);
    const publishedLayout=calculateLogoLayout(prepared.width,prepared.height,previewActive,placement,target,true);
    publishedHeight=publishedLayout.outputHeight;
    selectionStyle={
      left:`${editLayout.visibleLeft/profile.width*100}%`,
      top:`${editLayout.visibleTop/profile.height*100}%`,
      width:`${editLayout.visibleWidth/profile.width*100}%`,
      height:`${editLayout.visibleHeight/profile.height*100}%`,
    };
  } catch {}
  const background = active?.background || DEFAULT_BACKGROUND;
  const edits = active?.edits || DEFAULT_EDITS;
  const targetLabel = target === "desktop" ? "escritorio" : "móvil";
  const inputLabel = target === "desktop" ? "Seleccionar logo del cliente" : "Seleccionar imagen móvil";
  const gridStyle = {
    "--logo-grid-x": `${gridSize / profile.width * 100}%`,
    "--logo-grid-y": `${gridSize / profile.height * 100}%`,
  } as React.CSSProperties;
  const beginImageEdits = () => {
    if (!shown) return;
    if (!draft) {
      setDraft({ ...shown, mobile: shown.mobile ? { ...shown.mobile } : undefined });
      onBusyChange(true);
    }
    trackLogoEvent("EDIT_STARTED", "INICIO", { tool:"transparencia-recorte" });
    setActiveTool("edits");
  };
  const cancelImageEdits = () => { trackLogoEvent("EDIT_CANCELLED", "OK", { tool:"transparencia-recorte" }); setDraft(null); setActiveTool(null); onBusyChange(false); };
  const applyImageEdits = () => {
    if (draft) onChange(draft);
    trackLogoEvent("EDIT_APPLIED", "OK", { tool:"transparencia-recorte" });
    setDraft(null); setActiveTool(null); onBusyChange(false);
  };
  const editableCanvas = active && <div ref={editableFrame} className={`client-logo-editable-frame is-${placement} is-${editorMode}${previewTransform?" is-adjusting":""}`}>
    <canvas ref={canvas} width={profile.width} height={profile.height} aria-label={`Vista previa editable ${targetLabel}`}
      onPointerDown={beginMove} onPointerMove={updatePointer} onPointerUp={finishPointer} onPointerCancel={cancelPreview}/>
    {editorMode === "edit" && showGrid && <div className="client-logo-grid" style={gridStyle} aria-hidden="true">
      <span className="client-logo-ruler is-horizontal"/><span className="client-logo-ruler is-vertical"/>
      <span className="client-logo-center-guide is-x"/><span className="client-logo-center-guide is-y"/>
      {snapGuides.x&&<span className={`client-logo-snap-guide is-x is-${snapGuides.x}`}/>}
      {snapGuides.y&&<span className={`client-logo-snap-guide is-y is-${snapGuides.y}`}/>}
      <small>{profile.width} × {profile.height} px</small>
    </div>}
    <span className="client-logo-frame-size">{profile.width} × {editorMode === "preview" && placement === "banner" ? publishedHeight : profile.height} px</span>
    {editorMode === "edit" && selectionStyle && <div ref={selectionFrame} className="client-logo-selection-frame" style={selectionStyle}>
      {resizeHandles.map(([corner,label])=><button key={corner} type="button" tabIndex={-1} aria-hidden="true"
        title={`Redimensionar desde esquina ${label}`} className={`client-logo-resize-handle is-${corner}`}
        onPointerDown={beginResize} onPointerMove={updatePointer} onPointerUp={finishPointer} onPointerCancel={cancelPreview}/>) }
      {stretchHandles.map(([side,label,axis])=><button key={side} type="button" tabIndex={-1} aria-hidden="true"
        title={`Estirar desde el lado ${label}`} className={`client-logo-stretch-handle is-${side}`}
        onPointerDown={(event)=>beginStretch(event,side,axis)} onPointerMove={updatePointer} onPointerUp={finishPointer} onPointerCancel={cancelPreview}/>) }
    </div>}
  </div>;

  const formPreview = active && <div className={`client-logo-form-preview is-${target}${dark?" is-dark":""}`} style={formThemePreviewStyle(formTheme, dark)}>
    <div className="client-logo-preview-page">
      <header className="client-logo-preview-header"><span className="client-logo-preview-brand"><b>tw</b> Team-Work Postulaciones</span><span aria-hidden="true">☼</span></header>
      {placement === "banner" && editableCanvas}
      <div className="client-logo-preview-content">
        <section className="client-logo-preview-card">
          <div className="client-logo-preview-card-head">
            {placement === "card" && editableCanvas}
            <strong>Ingresa tus datos</strong><small>▣ Postulando a <b>{clientName}</b></small>
          </div>
          <div className="client-logo-preview-fields">{showJobOfferDetail&&<div className="client-logo-preview-offer"><b>▣</b><strong>Detalle de la oferta</strong><span>⌄</span></div>}<label>RUT <span>*</span><i>12345678-9</i><small>Sin puntos, con guión. Ej: 12345678-9</small></label><label>Número WhatsApp <span>*</span><i>56987654321</i><small>Formato: 569 seguido de 8 dígitos</small></label><button type="button" tabIndex={-1}>Continuar</button></div>
        </section>
      </div>
    </div>
  </div>;

  return <section className={`client-logo-editor${expanded ? " is-expanded" : ""}`}>
    <button type="button" role="switch" aria-checked={enabled} aria-label="Logo del cliente" className="client-logo-toggle" onClick={()=>{onToggle();trackLogoEvent("VISIBILITY_CHANGED", "OK", {tool:enabled?"desactivar":"activar"});}} disabled={busy || !!draft}>
      <span className={enabled ? "client-logo-switch is-on" : "client-logo-switch"}><span /></span>
      <span><strong>Identidad visual del formulario</strong><small>Configura un logo dentro de la tarjeta o un banner de ancho completo.</small></span>
    </button>
    {!enabled && themeControls && <div className="client-logo-theme-slot">{themeControls}</div>}
    {enabled && <div className="client-logo-body">
      {themeControls && <div className="client-logo-theme-slot">{themeControls}</div>}
      <fieldset className="client-logo-location" disabled={busy || !!draft}>
        <legend>Ubicación</legend>
        <label className={placement === "card" ? "is-selected" : ""}><input type="radio" name="logo-placement" value="card" checked={placement === "card"} onChange={() => void changePlacement("card")}/>
          Dentro de la tarjeta <small>Ideal para logos · 640 × 240</small></label>
        <label className={placement === "banner" ? "is-selected" : ""}><input type="radio" name="logo-placement" value="banner" checked={placement === "banner"} onChange={() => void changePlacement("banner")}/>
          Banner superior <small>De lado a lado bajo el encabezado</small></label>
      </fieldset>
      <div className="client-logo-tabs" role="tablist" aria-label="Versión de imagen">
        <button type="button" role="tab" aria-selected={target === "desktop"} className={target === "desktop" ? "is-active" : ""} onClick={() => {setDraft(null);setActiveTool(null);onBusyChange(false);setTarget("desktop");}}>Escritorio / tablet</button>
        <button type="button" role="tab" aria-selected={target === "mobile"} className={target === "mobile" ? "is-active" : ""} onClick={() => {setDraft(null);setActiveTool(null);onBusyChange(false);setTarget("mobile");}}>Móvil {value?.mobile ? "✓" : "(opcional)"}</button>
      </div>
      {target === "mobile" && value && !value.mobile && <div className="client-logo-mobile-fallback"><p>Si no cargas una versión móvil, se usará automáticamente la imagen de escritorio.</p>
        <button type="button" onClick={() => void adaptDesktopToMobile()} disabled={busy}>Adaptar escritorio como base</button></div>}
      {(target === "desktop" || value) && <label className="client-logo-upload"><span>{busy ? "Procesando imagen…" : active ? `Reemplazar versión ${targetLabel}` : `Seleccionar versión ${targetLabel}`}</span>
        <input aria-label={inputLabel} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" disabled={busy || !!draft} onChange={(e)=>{void upload(e.target.files?.[0]);e.target.value="";}}/>
        <small>PNG, JPG, SVG o WebP · Hasta 5 MB · Recomendado: {profile.label}</small></label>}
      {error && <p role="alert" className="client-logo-alert">{error}</p>}
      {active && <>
        <div className="client-logo-preview-heading"><span>{draft ? "EDICIÓN SIN APLICAR" : `FORMULARIO · ${targetLabel.toUpperCase()}`}</span><div className="client-logo-mode" role="group" aria-label="Modo del editor"><button type="button" className={editorMode === "edit" ? "is-active" : ""} onClick={()=>setEditorMode("edit")}>Editar</button><button type="button" className={editorMode === "preview" ? "is-active" : ""} onClick={()=>setEditorMode("preview")}>Vista previa</button></div></div>
        <div className="client-logo-toolbar" role="toolbar" aria-label="Herramientas de imagen">
          <span className="client-logo-toolbar-label">Herramientas</span>
          <ToolButton label="Tamaño y escala" active={activeTool === "size"} disabled={busy} onClick={()=>setActiveTool(activeTool === "size" ? null : "size")}><TuneRoundedIcon /></ToolButton>
          <ToolButton label="Cuadrícula, guías y alineación" active={activeTool === "guides"} disabled={busy} onClick={()=>setActiveTool(activeTool === "guides" ? null : "guides")}><GridOnRoundedIcon /></ToolButton>
          <ToolButton label="Fondo de la imagen" active={activeTool === "background"} disabled={busy || !!draft} onClick={()=>setActiveTool(activeTool === "background" ? null : "background")}><ColorLensOutlinedIcon /></ToolButton>
          <ToolButton label="Editar transparencia y recorte" active={activeTool === "edits"} disabled={busy} onClick={beginImageEdits}><AutoFixHighOutlinedIcon /></ToolButton>
          <span className="client-logo-tool-divider" />
          <ToolButton label="Restaurar original" disabled={busy || !!draft} onClick={()=>changeSettings({background:DEFAULT_BACKGROUND,edits:DEFAULT_EDITS,zoom:placement==="banner"?1:.85,x:0,y:0,stretchX:1,stretchY:1},"RESTORED")}><RestartAltRoundedIcon /></ToolButton>
          <ToolButton label={target === "mobile" ? "Quitar versión móvil" : "Quitar imagen"} disabled={busy || !!draft} onClick={()=>{if(target==="mobile"){if(value){const {mobile:_,...next}=value;onChange(next);trackLogoEvent("REMOVED", "OK", {tool:"móvil"});}}else{onChange(null);trackLogoEvent("REMOVED", "OK", {tool:"escritorio"});}}}><DeleteOutlineRoundedIcon /></ToolButton>
          <span className="client-logo-toolbar-spacer" />
          <ToolButton label={dark ? "Ver modo claro" : "Ver modo oscuro"} onClick={()=>setDark(!dark)}>{dark?<LightModeOutlinedIcon/>:<DarkModeOutlinedIcon/>}</ToolButton>
        </div>
        {activeTool === "size" && <div className="client-logo-tool-panel"><div className="client-logo-controls"><label>Tamaño <output>{Math.round((previewActive?.zoom||active.zoom)*100)}%</output><input type="range" aria-label="Tamaño del logo" min={.25} max={2} step={.01} value={previewActive?.zoom||active.zoom} disabled={busy}
          onChange={(e)=>previewSettings({zoom:Number(e.target.value)})} onPointerUp={commitPreview} onPointerCancel={cancelPreview} onKeyUp={commitPreview} onBlur={commitPreview}/></label></div></div>}
        {activeTool === "guides" && <div className="client-logo-tool-panel client-logo-guide-panel">
          <div className="client-logo-guide-settings"><label><input type="checkbox" checked={showGrid} onChange={(event)=>{setShowGrid(event.target.checked);trackLogoEvent("GUIDES_CHANGED", "OK", {tool:event.target.checked?"mostrar-cuadrícula":"ocultar-cuadrícula"});}}/> Mostrar cuadrícula y reglas</label><label><input type="checkbox" checked={snapEnabled} onChange={(event)=>{setSnapEnabled(event.target.checked);trackLogoEvent("GUIDES_CHANGED", "OK", {tool:event.target.checked?"activar-imán":"desactivar-imán"});}}/> Ajuste magnético</label><label>Separación <select aria-label="Separación de la cuadrícula" value={gridSize} onChange={(event)=>{const size=Number(event.target.value);setGridSize(size);trackLogoEvent("GUIDES_CHANGED", "OK", {tool:`cuadrícula-${size}`});}}><option value={25}>25 px</option><option value={50}>50 px</option><option value={100}>100 px</option></select></label></div>
          <div className="client-logo-alignment" role="group" aria-label="Alineación horizontal"><span>Horizontal</span><ToolButton label="Alinear a la izquierda" onClick={()=>alignArtwork("x","start")}><AlignHorizontalLeftRoundedIcon/></ToolButton><ToolButton label="Centrar horizontalmente" onClick={()=>alignArtwork("x","center")}><AlignHorizontalCenterRoundedIcon/></ToolButton><ToolButton label="Alinear a la derecha" onClick={()=>alignArtwork("x","end")}><AlignHorizontalRightRoundedIcon/></ToolButton></div>
          <div className="client-logo-alignment" role="group" aria-label="Alineación vertical"><span>Vertical</span><ToolButton label="Alinear arriba" onClick={()=>alignArtwork("y","start")}><AlignVerticalTopRoundedIcon/></ToolButton><ToolButton label="Centrar verticalmente" onClick={()=>alignArtwork("y","center")}><AlignVerticalCenterRoundedIcon/></ToolButton><ToolButton label="Alinear abajo" onClick={()=>alignArtwork("y","end")}><AlignVerticalBottomRoundedIcon/></ToolButton></div>
          <p className="client-logo-help">El imán detecta el centro y los bordes del marco. Al mover la imagen también avanza por pasos de la cuadrícula seleccionada.</p>
        </div>}
        {activeTool === "background" && <div className="client-logo-tool-panel"><fieldset className="client-logo-options" disabled={busy}><legend>Fondo de la imagen</legend><label>Tipo de fondo<select aria-label="Tipo de fondo del logo" value={background.type} onChange={(e)=>changeSettings({background:{...background,type:e.target.value as typeof background.type}},"BACKGROUND_CHANGED")}><option value="transparent">Sin fondo (transparente)</option><option value="solid">Color sólido</option><option value="gradient">Degradado</option></select></label>
          {background.type!=="transparent"&&<ColorField label="Color del fondo" value={background.color} onChange={(color)=>changeSettings({background:{...background,color}},"BACKGROUND_CHANGED")}/>} {background.type==="gradient"&&<><ColorField label="Segundo color" value={background.endColor} onChange={(endColor)=>changeSettings({background:{...background,endColor}},"BACKGROUND_CHANGED")}/><label>Dirección: {background.angle}°<input type="range" aria-label="Dirección del degradado" min={0} max={360} value={background.angle} onChange={(e)=>changeSettings({background:{...background,angle:Number(e.target.value)}})} onPointerUp={()=>trackLogoEvent("BACKGROUND_CHANGED", "OK", {tool:"dirección-degradado"})}/></label></>}</fieldset><p className="client-logo-help">“Sin fondo” conserva la transparencia existente; no elimina un fondo incluido en el archivo.</p></div>}
        {activeTool === "edits" && draft && <div className="client-logo-tool-panel"><fieldset className="client-logo-options"><legend>Transparencia y recorte · previsualiza antes de aplicar</legend><label className="client-logo-check"><input type="checkbox" checked={edits.removeBackground} onChange={(e)=>changeSettings({edits:{...edits,removeBackground:e.target.checked}})}/>Quitar fondo uniforme</label>
          {edits.removeBackground&&<><ColorField label="Color a eliminar" value={edits.color} onChange={(color)=>changeSettings({edits:{...edits,color}})}/><label>Tolerancia: {edits.tolerance}%<input type="range" aria-label="Tolerancia del fondo" min={0} max={100} value={edits.tolerance} onChange={(e)=>changeSettings({edits:{...edits,tolerance:Number(e.target.value)}})}/></label></>}
          <label className="client-logo-check"><input type="checkbox" checked={edits.cropTransparent} onChange={(e)=>changeSettings({edits:{...edits,cropTransparent:e.target.checked}})}/>Recortar márgenes transparentes</label><p className="client-logo-help">Elimina solo el color conectado a los bordes. El original se conserva.</p>
          <div className="client-logo-actions"><button type="button" onClick={cancelImageEdits}><CloseRoundedIcon/> Cancelar edición</button><button type="button" className="is-primary" onClick={applyImageEdits}><CheckRoundedIcon/> Aplicar edición</button></div></fieldset></div>}
        <p className={warning?"client-logo-warning":"client-logo-quality"} role="status">{warning||"Resolución adecuada para el tamaño elegido."}</p>
        {formPreview && (expanded && previewHost ? createPortal(formPreview, previewHost) : formPreview)}
        <p className="client-logo-meta"><strong>{active.fileName}</strong> · Original cargado: {active.width} × {active.height} px · Resultado guardado: {profile.width} × {placement === "banner" ? publishedHeight : profile.height} px</p>
        <p className="client-logo-limit"><strong>{placement === "banner" && target === "desktop" ? "Alto máximo publicado: 500 px." : `Marco máximo: ${profile.label}.`}</strong> En edición ves el marco máximo; la vista previa elimina el espacio vertical que no ocupa la imagen y muestra el resultado final.</p>
        <p className="client-logo-help">{editorMode === "edit" ? "Arrastra la imagen para encuadrarla. Las esquinas escalan proporcionalmente; los tiradores centrales estiran solo el ancho o el alto." : "Esta es la altura real que verá el postulante. Si redujiste la imagen, el formulario sube automáticamente."}</p>
      </>}
      {!value&&!busy&&<p className="client-logo-help">Carga primero la versión de escritorio/tablet. La versión móvil es opcional y puede derivarse automáticamente.</p>}
    </div>}
  </section>;
}
